// src/modules/bkg/components/BKGAgent/ChatPanel.tsx
//
// Chat panel component using mmagent-chat-ui for the BKG Agent.

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  Input,
  CircularProgress,
  Tooltip,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemContent,
  Divider,
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Alert,
} from "@mui/joy";
import SendIcon from "@mui/icons-material/Send";
import StopIcon from "@mui/icons-material/Stop";
import HistoryIcon from "@mui/icons-material/History";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import {
  ChatProvider,
  useChat,
  useConversations,
  useLoadConversation,
  useChatStore,
  ChatMessage,
} from "@metalmind-ab/mmagent-chat-ui";
import type { ChatMessageType } from "@metalmind-ab/mmagent-chat-ui";
import "@metalmind-ab/mmagent-chat-ui/styles.css";
import "../../styles/bkg-agent-theme.css";

import { useAuthHeaders } from "../../../../hooks/useAuthHeaders";
import { getApiBaseUrl } from "../../../../config/runtimeConfig";
import { buildChatResumeOptions } from "../../../../utils/chatResumeOptions";

interface ChatPanelProps {
  workspaceId: string;
}

/**
 * Chat panel component that provides the BKG agent chat interface.
 */
export const ChatPanel: React.FC<ChatPanelProps> = ({ workspaceId }) => {
  const [authHeaders, setAuthHeaders] = useState<Record<string, string> | null>(
    null,
  );
  const [authError, setAuthError] = useState<string | null>(null);
  // Track the last workspaceId this mount has seen so we can clear shared
  // chat state when the user switches workspaces while the component is
  // mounted. Local state (not localStorage) because localStorage is shared
  // across tabs -- seeding from it caused the first render in a new tab to
  // initialize against whichever workspace any other tab last touched.
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState<string | null>(
    null,
  );
  const getAuthHeaders = useAuthHeaders();

  // Get store actions to reset state when context changes
  const clearMessages = useChatStore((state) => state.clearMessages);
  const setConversationId = useChatStore((state) => state.setConversationId);
  const setConversations = useChatStore((state) => state.setConversations);

  // Clear shared Zustand store on mount to prevent cross-module conversation leakage.
  // The mmagent-chat-ui store is a global singleton shared by IDA, BKG, and other modules.
  // Without this, navigating from IDA to BKG in the same workspace would show IDA conversations.
  useEffect(() => {
    clearMessages();
    setConversationId(null);
    setConversations([], 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle workspace changes within BKG
  useEffect(() => {
    if (!workspaceId) return;

    const storedWorkspaceId = currentWorkspaceId;

    if (storedWorkspaceId && workspaceId !== storedWorkspaceId) {
      clearMessages();
      setConversationId(null);
      setConversations([], 0);
    }

    if (workspaceId !== storedWorkspaceId) {
      setCurrentWorkspaceId(workspaceId);
    }
  }, [
    workspaceId,
    currentWorkspaceId,
    clearMessages,
    setConversationId,
    setConversations,
  ]);

  // Fetch auth headers on mount
  useEffect(() => {
    const fetchHeaders = async () => {
      try {
        const headers = await getAuthHeaders();
        setAuthHeaders(headers);
      } catch (err) {
        setAuthError((err as Error).message);
      }
    };
    fetchHeaders();
  }, [getAuthHeaders]);

  // Wait for auth headers
  if (authHeaders === null && !authError) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size="sm" />
      </Box>
    );
  }

  if (authError) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography level="body-sm" color="danger">
          Authentication failed: {authError}
        </Typography>
      </Box>
    );
  }

  const apiBaseUrl = `${getApiBaseUrl()}/api/workspace/bkg`;

  const headers: Record<string, string> = {
    ...authHeaders,
    "X-Workspace-Id": workspaceId,
  };

  return (
    <ChatProvider
      key={`bkg-${workspaceId}`}
      config={{
        apiBaseUrl,
        headers,
        cancelSubmission: true,
        // Task #580 Phase 3b: SSE Last-Event-ID resume against BE broker.
        ...buildChatResumeOptions(apiBaseUrl),
      }}
    >
      <ChatContent />
    </ChatProvider>
  );
};

const ChatContent: React.FC = () => {
  const [inputValue, setInputValue] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Conversation history state
  const [historyOpen, setHistoryOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [hoveredConvoId, setHoveredConvoId] = useState<number | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  // mmagent-chat-ui hooks
  const {
    messages,
    sendMessage,
    isLoading,
    cancelStream,
    isStopping,
    stopError,
  } = useChat();
  const {
    conversations,
    search,
    loadMore,
    loading: conversationsLoading,
    deleteConversation,
    hasMore,
  } = useConversations();
  const { loadConversation, startNewConversation } = useLoadConversation();
  const conversationId = useChatStore((state) => state.conversationId);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Focus input when loading completes
  const prevIsLoading = useRef(isLoading);
  useEffect(() => {
    if (prevIsLoading.current && !isLoading) {
      inputRef.current?.focus();
    }
    prevIsLoading.current = isLoading;
  }, [isLoading]);

  // Global ESC key listener to cancel stream
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isLoading && !isStopping && cancelStream) {
        e.preventDefault();
        cancelStream();
      }
    };
    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  }, [isLoading, isStopping, cancelStream]);

  const handleSend = useCallback(async () => {
    if (!inputValue.trim() || isLoading) return;

    const message = inputValue.trim();
    setInputValue("");
    await sendMessage(message);
  }, [inputValue, isLoading, sendMessage]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  // Conversation history handlers
  const handleSearch = useCallback(
    (query: string) => {
      setSearchQuery(query);
      search(query);
    },
    [search],
  );

  const handleNewConversation = useCallback(() => {
    startNewConversation();
    setHistoryOpen(false);
  }, [startNewConversation]);

  const handleLoadConversation = useCallback(
    (id: number) => {
      loadConversation(id);
      setHistoryOpen(false);
    },
    [loadConversation],
  );

  const handleDeleteConversation = useCallback(
    (id: number) => {
      deleteConversation(id);
      setDeleteConfirmId(null);
    },
    [deleteConversation],
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "background.level1",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>
            <Typography level="title-sm" noWrap>
              BKG Assistant
            </Typography>
            <Typography level="body-xs" sx={{ color: "text.tertiary" }} noWrap>
              Biotech Knowledge Graph
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Conversation history">
              <IconButton
                variant="plain"
                color="neutral"
                size="sm"
                onClick={() => setHistoryOpen(true)}
              >
                <HistoryIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="New conversation">
              <IconButton
                variant="plain"
                color="primary"
                size="sm"
                onClick={handleNewConversation}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>

      {/* Messages */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          p: 1.5,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        {messages.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              color: "text.tertiary",
              py: 4,
            }}
          >
            <Typography level="body-sm">
              I can help you explore companies, venture capital, and catalyst
              events in the Biotech Knowledge Graph.
            </Typography>
            <Typography level="body-xs" sx={{ mt: 1 }}>
              Ask me about companies, investors, pipeline assets, or events.
            </Typography>
          </Box>
        ) : (
          messages.map((message: ChatMessageType) => (
            <ChatMessage key={message.id} message={message} />
          ))
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input */}
      <Box
        sx={{
          p: 1.5,
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: "background.level1",
        }}
      >
        <Box sx={{ display: "flex", gap: 1 }}>
          <Input
            ref={inputRef}
            placeholder="Ask about companies, investors, events..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            sx={{ flex: 1 }}
            size="sm"
          />
          {isLoading ? (
            <Tooltip
              title={isStopping ? "Stopping..." : "Stop generating (ESC)"}
            >
              <IconButton
                onClick={cancelStream}
                disabled={isStopping}
                color="danger"
                variant="soft"
                size="sm"
              >
                <StopIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <IconButton
              onClick={handleSend}
              disabled={!inputValue.trim()}
              color="primary"
              variant="solid"
              size="sm"
            >
              <SendIcon />
            </IconButton>
          )}
        </Box>
        {stopError && <Alert color="danger">{stopError}</Alert>}
      </Box>

      {/* Conversation history drawer */}
      <Drawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        anchor="left"
        size="sm"
      >
        <Box sx={{ p: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography level="title-lg">Conversations</Typography>
            <IconButton
              variant="plain"
              color="neutral"
              onClick={() => setHistoryOpen(false)}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>
        <Box sx={{ px: 2, pb: 2 }}>
          <Input
            startDecorator={<SearchIcon />}
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            size="sm"
          />
        </Box>
        <Divider />
        <List sx={{ py: 0 }}>
          <ListItem>
            <ListItemButton onClick={handleNewConversation}>
              <AddIcon sx={{ mr: 1 }} />
              <ListItemContent>
                <Typography level="body-sm">New conversation</Typography>
              </ListItemContent>
            </ListItemButton>
          </ListItem>
          <Divider />
          {conversationsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size="sm" />
            </Box>
          ) : conversations.length === 0 ? (
            <ListItem>
              <ListItemContent>
                <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
                  No conversations yet
                </Typography>
              </ListItemContent>
            </ListItem>
          ) : (
            <>
              {conversations.map((convo) => (
                <ListItem
                  key={convo.id}
                  onMouseEnter={() => setHoveredConvoId(convo.id)}
                  onMouseLeave={() => setHoveredConvoId(null)}
                  endAction={
                    hoveredConvoId === convo.id ? (
                      <IconButton
                        aria-label="Delete"
                        size="sm"
                        color="danger"
                        variant="plain"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirmId(convo.id);
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    ) : undefined
                  }
                >
                  <ListItemButton
                    selected={convo.id === conversationId}
                    onClick={() => handleLoadConversation(convo.id)}
                  >
                    <ListItemContent>
                      <Typography level="body-sm">
                        {convo.title || `Conversation ${convo.id}`}
                      </Typography>
                      <Typography
                        level="body-xs"
                        sx={{ color: "text.tertiary" }}
                      >
                        {new Date(convo.createdAt).toLocaleDateString()}
                      </Typography>
                    </ListItemContent>
                  </ListItemButton>
                </ListItem>
              ))}
              {hasMore && (
                <ListItem>
                  <ListItemButton onClick={loadMore}>
                    <ListItemContent>
                      <Typography level="body-sm" sx={{ color: "primary.500" }}>
                        Load older...
                      </Typography>
                    </ListItemContent>
                  </ListItemButton>
                </ListItem>
              )}
            </>
          )}
        </List>
      </Drawer>

      {/* Delete confirmation modal */}
      <Modal
        open={deleteConfirmId !== null}
        onClose={() => setDeleteConfirmId(null)}
      >
        <ModalDialog variant="outlined" role="alertdialog">
          <DialogTitle>Delete conversation?</DialogTitle>
          <DialogContent>
            This will permanently delete this conversation and all its messages.
          </DialogContent>
          <DialogActions>
            <Button
              variant="solid"
              color="danger"
              onClick={() => {
                if (deleteConfirmId) {
                  handleDeleteConversation(deleteConfirmId);
                }
              }}
            >
              Delete
            </Button>
            <Button
              variant="plain"
              color="neutral"
              onClick={() => setDeleteConfirmId(null)}
            >
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default ChatPanel;
